import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import {
  CreateButton,
  Title,
  useDataProvider,
  useNotify,
  usePermissions,
  useStore,
} from "react-admin";
import { formatEventCardDateRange } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";
import { navigateToBaseRoute } from "@/utils/route-utility";
import type { HouseVisitEventRecord } from "../types";

const HouseVisitsDashboard = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const [, setCurrentEvent] = useStore<HouseVisitEventRecord | null>(
    "currentHouseVisitEvent",
    null
  );
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<HouseVisitEventRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dataProvider
      .getList("houseVisitEvents", {
        pagination: { page: 1, perPage: 200 },
        sort: { field: "fromDate", order: "DESC" },
        filter: {},
      })
      .then(({ data }) => {
        if (!cancelled) setEvents(data as HouseVisitEventRecord[]);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setEvents([]);
          notify(err.message || "Failed to load events", { type: "warning" });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataProvider, notify]);

  const openEvent = (event: HouseVisitEventRecord) => {
    setCurrentEvent(event);
    navigateToBaseRoute("houseVisits", String(event.id));
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1100, mx: "auto", width: "100%" }}>
      <Title title="House Visits" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: { xs: "1.25rem", md: "1.6rem" }, letterSpacing: "-0.02em" }}
          >
            House Visits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pick an event to plan slots and run today&apos;s route.
          </Typography>
        </Box>
        {hasPermission(permissions, "houseVisitEvents.create") ? (
          <CreateButton resource="houseVisitEvents" label="Create event" />
        ) : null}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No house visit events yet.
          {hasPermission(permissions, "houseVisitEvents.create") ? (
            <Button
              sx={{ ml: 1 }}
              size="small"
              onClick={() => {
                window.location.hash = "#/houseVisitEvents/create";
              }}
            >
              Create one
            </Button>
          ) : null}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {events.map((event) => (
            <Grid key={String(event.id)} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardActionArea onClick={() => openEvent(event)} sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {event.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hijri {event.hijriYear}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {formatEventCardDateRange(event.fromDate, event.toDate)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default HouseVisitsDashboard;
